import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { useAuth } from '../contexts/AuthContext';
import { useDiscount } from '../contexts/DiscountContext';
import { productNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddCard from '../components/AddCard';
import PageHeader from '../components/PageHeader';
import AddDiscount from '../components/AddDiscount';
import InfiniteScroll from 'react-infinite-scroll-component';

function DiscountList() {
    const { getAllDiscount, getSingleDiscount } = useDiscount();
    const { toast, restaurantId, auth, hasPermission } = useAuth();

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);

    const [discountList, setDiscountList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editDiscountId, setEditDiscountId] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [discountDetail, setDiscountDetail] = useState({
        name: '',
        bill_type: "",
        percentage: "",
        item_type: "category",
        type: "all",
        is_active: 1,
        category_id: "",
        products: [],
        restaurant_id: restaurantId
    })
    const [filter, setFilter] = useState({
        limit: 50,
        filter: null,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
    });

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
        }
    }, [restaurantId])

    const handleFilterChange = async (newFilter) => {
        setDiscountList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0, });
        setHasMore(true);
    };

    useEffect(() => {
        if (filter) {
            fetchDiscount(filter);
        }
    }, [filter])

    const fetchDiscount = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true);

                const data = await getAllDiscount(filter);
                if (data.discount.length !== 0 && data.totalCount !== discountList.length) {
                    setDiscountList([...discountList, ...data.discount]);
                    setTotalRecords(data.totalCount)
                    setTotalRenderedRecords(discountList.length)
                }

                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching discount', error);
        }
    };

    const handleCardClick = (id) => {
        if(!canUpdate && !canDelete) return;
        setEditMode(true);
        setEditDiscountId(id);
        setVisible(false);
    };

    const handleLoadMore = () => {
        if (discountList.length === totalRecords) {
            setHasMore(false);
        } else if (discountList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchDiscountProps = async (newDiscountId) => {
        try {
            if (editMode && editDiscountId) {
                const discount = await getSingleDiscount(editDiscountId);
                const discountIndex = discountList.findIndex((u) => u.id === editDiscountId);

                if (discountIndex !== -1) {
                    if (!discount) {
                        const updatedDiscountList = [...discountList];
                        updatedDiscountList.splice(discountIndex, 1);

                        setDiscountList(updatedDiscountList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedDiscountList = [...discountList];
                        updatedDiscountList[discountIndex] = discount;

                        setDiscountList(updatedDiscountList);
                    }
                } else {
                    setDiscountList([...discountList, discount]);
                }
            } else if (visible) {
                const updatedDiscountList = [...discountList];
                const formattedPercentage = parseFloat(discountDetail.percentage).toFixed(2);

                updatedDiscountList.unshift({ ...discountDetail, id: newDiscountId, percentage: formattedPercentage });
                setDiscountList(updatedDiscountList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching discounts.', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];

        discountList && discountList.forEach((discount, index) => {
            currentRow.push(
                <div key={discount.id} className="d-flex">
                    {/* arcticons:money-manager-ktwapps */}
                    {/* solar:wallet-money-linear */}
                    <Card
                        header={<Icon icon="arcticons:money-manager-ktwapps" height={100} width={70} style={{ color: discount.is_active === 1 ? 'seagreen' : 'rgb(206, 48, 36)' }} />}
                        title={
                            <>
                                <div className='modifier-name' title={discount.name}>{discount.name}</div>
                            </>}
                        className={`${discount.is_active === 1 ? 'modifiers-list-card' : 'inactive_discount'}`}
                        onClick={() => handleCardClick(discount.id)}
                    />
                </div>
            );

            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
        });

        return rows;
    };

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Discounts'} />
                <div className='px-4 py-2'>
                    {(visible || (editMode && editDiscountId)) &&
                        <AddDiscount
                            id={editDiscountId}
                            visible={visible}
                            editDiscountId={editDiscountId}
                            editMode={editMode}
                            discountDetail={discountDetail}
                            setEditDiscountId={setEditDiscountId}
                            setVisible={setVisible}
                            setEditMode={setEditMode}
                            fetchDiscount={fetchDiscountProps}
                            setDiscountDetail={setDiscountDetail}
                            canCreate={canCreate}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                        />
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <Toast ref={toast} />
                            <div className="course-list">
                                <InfiniteScroll
                                    dataLength={discountList.length}
                                    next={handleLoadMore}
                                    hasMore={hasMore}
                                    className="card-container"
                                >
                                    {canCreate && <AddCard color="#536347" label="Add Discount" setVisible={setVisible} />}
                                    {discountList.length !== 0 && renderCardsInRows()}
                                </InfiniteScroll>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default DiscountList;
